import { useState, useEffect } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Popover,
  PopoverBody,
  Spinner,
} from 'reactstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiFileDownloadOutline, mdiFilterOutline, mdiDotsGrid } from '@mdi/js';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import './OverallRating.scss';
import sortIcon from '../../../../assets/images/calibration/shortIcon.png';
import filterIcon from '../../../../assets/images/calibration/filterIcon.png';
import optionMenu from '../../../../assets/images/calibration/optionMenu.png';
import ChangeReason from '../ChangeReason/ChangeReason';
import { getAuthUser } from '../../../../utils/auth.util';
import { ExportUtil } from '../../../../utils';
import { Messages } from '../../../../common/messages';
import ClearAllIcon from '../../../../assets/icons/clear-all.svg';

function OverallRating({
  refetchAllData,
  calibrationData,
  isPending,
  isReadOnly,
  toggle,
  filterData,
  resetFilterData,
  updateSubject,
  updateSubjectIsPending,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sessionId } = useParams();
  const user = getAuthUser();
  const [dataDrag, setDataDrag] = useState({ lists: {} });
  const [changeReason, setChangeReason] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [draggedEmployee, setDraggedEmployee] = useState({});
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  const [filterItem, setFilterItem] = useState({});

  // Add useEffect to handle outside clicks
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        openPopoverId &&
        !event.target.closest('.popover') &&
        !event.target.closest(`#popover-button-${openPopoverId}`)
      ) {
        setOpenPopoverId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopoverId]);

  useEffect(() => {
    setFilterItem(filterData);
  }, [filterData]);

  const removeFilterItem = (keyToReset, emptyValue) => {
    const updated = {
      ...filterItem,
      [keyToReset]: emptyValue,
    };
    setFilterItem(updated);
    resetFilterData(updated);
  };

  const clearFilter = () => {
    setFilterItem({});
    resetFilterData({});
  };

  const hadleCheckBoxChange = (empCd, isChecked) => {
    const payload = {
      sessionId,
      empCd,
      isChecked: isChecked ? 1 : 0,
    };
    updateSubject(payload);
  };

  useEffect(() => {
    if (calibrationData) {
      const ratingsMap = {};
      const response = calibrationData?.result;
      if (response && response?.performance_header?.length > 0) {
        response.performance_header.forEach(item => {
          const rating = item?.Calibration_Session_Rating
            ? Math.round(item?.Calibration_Session_Rating)
            : Math.round(item?.Overall_Rating_Manager);
          if (!rating || rating < 1 || rating > 5) return;

          if (!ratingsMap[rating]) ratingsMap[rating] = [];

          ratingsMap[rating].push({
            id: item?.EmpCd?.trim(),
            content: item?.EmpName?.trim(),
            isChecked: item?.Is_Checked,
          });
        });

        const rating1 = ratingsMap[1] || [];
        const rating2 = ratingsMap[2] || [];
        const rating3 = ratingsMap[3] || [];
        const rating4 = ratingsMap[4] || [];
        const rating5 = ratingsMap[5] || [];

        const lists = {
          lists: {
            rating5: {
              title: 'Rating : 5',
              items: rating5,
            },
            rating4: {
              title: 'Rating : 4',
              items: rating4,
            },
            rating3: {
              title: 'Rating : 3',
              items: rating3,
            },
            rating2: {
              title: 'Rating : 2',
              items: rating2,
            },
            rating1: {
              title: 'Rating : 1',
              items: rating1,
            },
          },
        };
        setDataDrag(lists);
      } else {
        const lists = {
          lists: {
            rating5: {
              title: 'Rating : 5',
              items: [],
            },
            rating4: {
              title: 'Rating : 4',
              items: [],
            },
            rating3: {
              title: 'Rating : 3',
              items: [],
            },
            rating2: {
              title: 'Rating : 2',
              items: [],
            },
            rating1: {
              title: 'Rating : 1',
              items: [],
            },
          },
        };
        setDataDrag(lists);
      }
    }
  }, [calibrationData]);

  const togglePopover = pid => {
    setOpenPopoverId(prev => (prev === pid ? null : pid));
  };

  const toggleChangeReason = () => {
    setChangeReason(!changeReason);
  };

  const handleChangeReasonDone = () => {
    if (pendingDragUpdate) {
      setDataDrag(pendingDragUpdate);
      setPendingDragUpdate(null);
    }
    toggleChangeReason();
  };

  const onDragEnd = result => {
    // If readonly, don't allow any drag operations
    if (isReadOnly) {
      return;
    }

    const { source, destination } = result;

    if (!destination) {
      return;
    }

    // If dropped in the same spot
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceList = dataDrag?.lists[source.droppableId];
    const destList = dataDrag?.lists[destination.droppableId];

    const sourceItems = Array.from(sourceList.items);
    const [movedItem] = sourceItems.splice(source.index, 1);

    const destItems = Array.from(destList.items);
    destItems.splice(destination.index, 0, movedItem);

    const input = {
      sessionId,
      empCd: movedItem?.id,
      pcmRating: parseInt(destination?.droppableId.replace('rating', ''), 10),
      performanceRating: parseInt(
        destination?.droppableId.replace('rating', ''),
        10,
      ),
      overallRating: parseInt(
        destination?.droppableId.replace('rating', ''),
        10,
      ),
      userId: user?.id,
      status: 'Update_Rating',
    };
    setDraggedEmployee(input);

    // Store the update in pending state instead of applying immediately
    setPendingDragUpdate({
      lists: {
        ...dataDrag.lists,
        [source.droppableId]: {
          ...sourceList,
          items: sourceItems,
        },
        [destination.droppableId]: {
          ...destList,
          items: destItems,
        },
      },
    });

    toggleChangeReason();
  };

  const handleViewPerformance = id => {
    navigate(`/hr/performance/${id}`, {
      state: { from: location.pathname, activeTab: 1 },
    });
    togglePopover(id);
  };

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = calibrationData?.result?.performance_header || [];
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const formattedData = exportData?.map(item => ({
      EmpCd: item?.EmpCd,
      EmpName: item?.EmpName,
      Designation: item?.Designation,
      Manager_Rating: item?.Overall_Rating_Manager
        ? Math.round(item?.Overall_Rating_Manager)
        : 'Rating not yet provided',
      Calibration_Rating: item?.Calibration_Session_Rating
        ? Math.round(item?.Calibration_Session_Rating)
        : 'Rating not yet provided',
    }));

    ExportUtil.downloadExcel(formattedData, {
      fileName: `Performance_Calibration_${timesTamp}.xlsx`,
      columns: {
        EmpCd: 'Employee Code',
        EmpName: 'Employee Name',
        Designation: 'Designation',
        Manager_Rating: 'Manager Rating',
        Calibration_Rating: 'Calibration Rating',
      },
    });
  };

  return (
    <>
      <Container fluid>
        <Card className="pc-card mt-3">
          <CardHeader
            className="d-flex justify-content-between align-items-center pt-2 pb-2"
            style={{ borderRadius: '10px 10px 0 0' }}
          >
            <div className="d-flex gap-2">
              <span className="fs-5">
                <strong>Performance & Calibration</strong>
              </span>
            </div>
            <div className="d-flex gap-2">
              <div className="d-flex gap-1 align-items-center">
                {filterItem?.companyCode?.Code && (
                  <div
                    key={filterItem.companyCode.Code}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem?.companyCode?.Description}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('companyCode', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.employee?.name && (
                  <div
                    key={filterItem.employee.name}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.employee.name}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('employee', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.manager?.name && (
                  <div
                    key={filterItem.manager.name}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.manager.name}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('manager', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.department?.Description && (
                  <div
                    key={filterItem?.department?.Description}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem?.department?.Description}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('department', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {filterItem?.classification?.label && (
                  <div
                    key={filterItem.classification.label}
                    className="filter-bubble px-2"
                  >
                    <span className="filter-bubble-text">
                      {filterItem.classification.label}
                    </span>
                    <span
                      className="filter-bubble-close"
                      onClick={() => removeFilterItem('classification', null)}
                    >
                      &times;
                    </span>
                  </div>
                )}
                {(filterItem.companyCode ||
                  filterItem.manager ||
                  filterItem.employee ||
                  filterItem.department ||
                  filterItem.classiffication) && (
                  <div
                    className="clear-all-btn px-2"
                    onClick={() => clearFilter()}
                  >
                    <span className="clear-all-btn-text">Clear All</span>
                    <img src={ClearAllIcon} alt="Clear All" />
                  </div>
                )}
              </div>
              {isPending && (
                <Spinner
                  size="lg"
                  color="primary"
                  className="filter-dropdown-spinner"
                />
              )}
              <Button className="btn btn-light" onClick={() => toggle()}>
                <span>Filter</span>
                <Icon path={mdiFilterOutline} size={0.8} />
              </Button>
              <Button className="btn btn-light" onClick={handleExportExcel}>
                <span>Report</span>
                <Icon path={mdiFileDownloadOutline} size={0.8} />
              </Button>
            </div>
          </CardHeader>
          <CardBody className="drag-body">
            {!isPending && !calibrationData?.result?.Session_Id && (
              <div className="d-flex justify-content-center align-items-center text-danger fw-bold pt-2">
                {Messages.NO_CALIBRATION}
              </div>
            )}
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="drag-cards">
                {Object.entries(dataDrag?.lists).map(
                  ([listId, list], cardIndex) => (
                    <Droppable droppableId={listId} key={listId}>
                      {droppableProvided => (
                        <div
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                          className={`drag-card ${cardIndex < 4 ? 'border-right' : ''} ${cardIndex === 0 ? 'border-left-radius' : ''} ${cardIndex === 4 ? 'border-right-radius' : ''}`}
                        >
                          <div className="drag-card-header">
                            <div className="info">
                              <div className="fs-6 fw-6">{list.title}</div>
                              <Badge className="fw-5 fs-6 badge-count">
                                {list?.items?.length || 0}
                              </Badge>
                            </div>
                            <div className="action">
                              <Button
                                className="no-outline close-btn"
                                size="sm"
                                outline
                              >
                                <img src={sortIcon} alt="" width={10} />
                              </Button>
                              <Button className="no-outline" size="sm" outline>
                                <img src={filterIcon} alt="" width={16} />
                              </Button>
                            </div>
                          </div>
                          {list?.items.map((item, index) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id}
                              index={index}
                              isDragDisabled={isReadOnly} // Add this prop
                            >
                              {(draggableProvided, snapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  className={`drag-card-item ${item.id === '2' ? 'active' : ''} ${item.isChecked ? 'card-bg-color' : ''}`}
                                  style={{
                                    userSelect: 'none',
                                    boxShadow: snapshot.isDragging
                                      ? '0 4px 8px rgba(0,0,0,0.2)'
                                      : '0 1px 2px rgba(0,0,0,0.1)',
                                    ...draggableProvided.draggableProps.style,
                                  }}
                                >
                                  <div className="info">
                                    <Icon path={mdiDotsGrid} size={0.8} />
                                    <span>
                                      <strong>{item.content}</strong>
                                    </span>
                                  </div>
                                  <div className="action">
                                    <Input
                                      type="checkbox"
                                      className="green-checkbox"
                                      checked={item.isChecked || false}
                                      onChange={e =>
                                        hadleCheckBoxChange(
                                          item.id,
                                          e.target.checked,
                                        )
                                      }
                                      disabled={updateSubjectIsPending}
                                    />
                                    <Button
                                      size="sm"
                                      className="no-outline"
                                      outline
                                      id={`popover-button-${item.id}`}
                                      onClick={() => togglePopover(item.id)}
                                    >
                                      <img src={optionMenu} alt="" />
                                    </Button>
                                    <Popover
                                      placement="right"
                                      isOpen={openPopoverId === item.id}
                                      target={`popover-button-${item.id}`}
                                      toggle={() => togglePopover(item.id)}
                                    >
                                      <PopoverBody className="performance-popover-body">
                                        <Button
                                          className="btn-performance"
                                          onClick={() => {
                                            handleViewPerformance(item.id);
                                          }}
                                        >
                                          View Performance
                                        </Button>
                                      </PopoverBody>
                                    </Popover>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ),
                )}
              </div>
            </DragDropContext>
          </CardBody>
        </Card>
      </Container>
      <ChangeReason
        isOpen={changeReason}
        toggle={toggleChangeReason}
        draggedEmployee={draggedEmployee}
        onDone={handleChangeReasonDone}
        refetchAllData={refetchAllData}
      />
    </>
  );
}

export default OverallRating;
